// Inline-SVG charts tuned to the brand palette:
//   #DC904C orange · #4671c6 vivid blue · #51669e pale · #519E82 green
// Goal: feel like a polished dashboard, not a default Tailwind admin.

type BarPoint = { label: string; value: number };

export function BarChart({
  data,
  height = 150,
  formatValue = (v) => v.toString(),
}: {
  data: BarPoint[];
  height?: number;
  formatValue?: (v: number) => string;
}) {
  if (data.length === 0) {
    return (
      <p className="text-xs text-white/50 py-8 text-center">
        Pas encore de données.
      </p>
    );
  }

  const max = Math.max(...data.map((d) => d.value), 1);
  const min = Math.min(...data.map((d) => d.value));
  const baselineY = height - 18;
  const usableH = baselineY - 8;
  const barW = 100 / data.length;
  const gradId = 'barGrad';
  const glowId = 'barGlow';

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height: `${height}px` }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F5B07A" />
            <stop offset="55%" stopColor="#DC904C" />
            <stop offset="100%" stopColor="#4671C6" />
          </linearGradient>
          <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="0.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Baseline */}
        <line
          x1="0"
          x2="100"
          y1={baselineY}
          y2={baselineY}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="0.4"
        />

        {data.map((d, i) => {
          const h = Math.max((d.value / max) * usableH, 1);
          const x = i * barW + barW * 0.18;
          const w = barW * 0.64;
          const y = baselineY - h;
          return (
            <g key={i} filter={`url(#${glowId})`}>
              <rect
                x={x}
                y={y}
                width={w}
                height={h}
                rx={1.4}
                fill={`url(#${gradId})`}
              />
              <text
                x={x + w / 2}
                y={height - 4}
                textAnchor="middle"
                fontSize={4.5}
                fill="rgba(255,255,255,0.55)"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="flex justify-between text-[11px] mt-1 px-1">
        <span className="text-white/45">min {formatValue(min)}</span>
        <span className="font-semibold text-brand-orange">
          max {formatValue(max)}
        </span>
      </div>
    </div>
  );
}

export function Sparkline({
  data,
  height = 36,
}: {
  data: number[];
  height?: number;
}) {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return { x, y };
  });
  const linePath = pts.map((p) => `${p.x},${p.y}`).join(' ');
  const areaPath = [
    `M ${pts[0].x},${height}`,
    ...pts.map((p) => `L ${p.x},${p.y}`),
    `L ${pts[pts.length - 1].x},${height} Z`,
  ].join(' ');
  const gradId = 'sparkArea';

  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height: `${height}px` }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#DC904C" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#DC904C" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <polyline
        points={linePath}
        fill="none"
        stroke="#F5B07A"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Three-tier brand colours mapped to performance bands. The track is a faint
// white so the filled bar always pops on the dark surface.
function tierColors(pct: number): { from: string; to: string } {
  if (pct >= 75) return { from: '#82C7AE', to: '#519E82' };
  if (pct >= 50) return { from: '#F5B07A', to: '#DC904C' };
  return { from: '#7C8FC3', to: '#51669E' };
}

export function PercentileBar({
  label,
  percentile,
}: {
  label: string;
  percentile: number;
}) {
  const pct = Math.max(0, Math.min(100, percentile));
  const { from, to } = tierColors(pct);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-xs">
        <span className="text-white/70">{label}</span>
        <span className="font-semibold tabular-nums">
          {pct}
          <sup>e</sup> perc.
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${from}, ${to})`,
            boxShadow: `0 0 8px ${to}55`,
          }}
        />
      </div>
    </div>
  );
}
